import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import EmptyState from '@components/Common/EmptyState';
import { useToast } from '@components/Common/Toast/ToastProvider';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import { Badge, Box, IconButton, Popover, Typography } from '@mui/material';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { RootState } from '@store/rootReducer';
import './index.scss';
import { formatDate, parseDate } from '@helpers/date';

const POLL_INTERVAL_MS = 30_000;
// Fetch a wider window than the popover used to: high-volume sign-ups are collapsed into one
// summary row below, so pulling 25 surfaces the quieter important events that a batch of
// sign-ups would otherwise have pushed past the first ten.
const PAGE_SIZE = 25;

export interface AdminNotification {
  id: string;
  type: string;
  title: string;
  body?: string;
  link?: string;
  isRead: boolean;
  createdAt?: string;
}

/**
 * Per-type icon tile. `tone` is the glyph colour, `tint` the tile fill — both
 * tokens, so the tile tracks the palette. Unknown types fall back to the
 * neutral accent rather than going untinted.
 */
const TYPE_PRESENTATION: Record<string, { Icon: typeof PersonAddAltOutlinedIcon; tone: string; tint: string }> = {
  NEW_EMPLOYER_SIGNUP:      { Icon: BusinessOutlinedIcon,    tone: 'var(--acc)',    tint: 'var(--acc-tint)' },
  NEW_JOBSEEKER_SIGNUP:     { Icon: PersonAddAltOutlinedIcon, tone: 'var(--acc)',   tint: 'var(--acc-tint)' },
  NEW_JOB_POSTED:           { Icon: WorkOutlineOutlinedIcon, tone: 'var(--teal)',   tint: 'var(--teal-tint)' },
  NEW_JOB_APPLICATION:      { Icon: HowToRegOutlinedIcon,    tone: 'var(--green)',  tint: 'var(--green-tint)' },
  NEW_COMPANY_REQUEST:      { Icon: BusinessOutlinedIcon,    tone: 'var(--amber)',  tint: 'var(--amber-tint)' },
  NEW_SUBSCRIPTION:         { Icon: CreditCardOutlinedIcon,  tone: 'var(--gold)',   tint: 'var(--gold-tint)' },
  FOLLOW_UP_DUE:            { Icon: EventAvailableOutlinedIcon, tone: 'var(--amber)', tint: 'var(--amber-tint)' },
  BLOG_POST_PUBLISHED:      { Icon: ArticleOutlinedIcon,     tone: 'var(--indigo)', tint: 'var(--indigo-tint)' },
  RESOURCE_GUIDE_PUBLISHED: { Icon: ArticleOutlinedIcon,     tone: 'var(--indigo)', tint: 'var(--indigo-tint)' },
};

const DEFAULT_PRESENTATION = { Icon: AutoAwesomeOutlinedIcon, tone: 'var(--acc)', tint: 'var(--acc-tint)' };

const GET_ADMIN_UNREAD_COUNT = `{ getAdminUnreadNotificationCount }`;

const buildGetAdminNotificationsQuery = (page: number, size: number) => `
  { getAdminNotifications(page: ${page}, size: ${size}) {
      id type title body link isRead createdAt
  } }
`;

const MARK_ALL_ADMIN_READ = `mutation { markAllAdminNotificationsRead }`;

interface NotificationBellProps {
  userId?: string;
}

const NotificationBellComponent: React.FC<NotificationBellProps> = ({ userId }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { showToast } = useToast();

  const fetchUnreadCount = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetchGraphQLApiData(GET_ADMIN_UNREAD_COUNT);
      const count = res?.data?.getAdminUnreadNotificationCount;
      if (typeof count === 'number') setUnreadCount(count);
    } catch {
      // Stop polling on auth error — count stays at current value
      if (pollRef.current) clearInterval(pollRef.current);
    }
  }, [userId]);

  useEffect(() => {
    fetchUnreadCount();
    pollRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchUnreadCount]);

  // Chrome notifications. In the background the service worker shows them; with a console tab
  // focused FCM hands them here instead, so the count moves now rather than at the next poll and
  // the alert is read out as a toast.
  const onForegroundMessage = useCallback(
    ({ title }: { title: string }) => {
      fetchUnreadCount();
      showToast(title, 'info');
    },
    [fetchUnreadCount, showToast],
  );
  usePushNotifications({ userId, onForegroundMessage });

  // Seed the admin's notification-preference doc once per session. The backend defaults new
  // admins to "subscribed to all events", but only persisted subscribers receive notifications,
  // so this guarantees events (e.g. Premium Hiring requests) actually reach the bell.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!userId || seededRef.current) return;
    seededRef.current = true;
    fetchGraphQLApiData(`{ getAdminNotificationPreferences { userId } }`).catch(() => {});
  }, [userId]);

  const handleBellClick = async (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setLoading(true);
    try {
      const res = await fetchGraphQLApiData(buildGetAdminNotificationsQuery(0, PAGE_SIZE));
      const list: AdminNotification[] = res?.data?.getAdminNotifications ?? [];
      setNotifications(list);
      if (unreadCount > 0) {
        setUnreadCount(0);
        fetchGraphQLApiData(MARK_ALL_ADMIN_READ).catch(() => {});
      }
    } catch {
      // keep whatever was shown
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleItemClick = (notif: AdminNotification) => {
    handleClose();
    if (notif.link) navigate(notif.link);
  };

  /*
   * The popover polls, and it is still the record. Chrome notifications are the other half now:
   * the service worker shows the same admin_notifications while the console is in the background,
   * and usePushNotifications (above) registers for them once Chrome has granted permission. The
   * controls are the banner in the app shell and the Chrome notifications row on
   * /notifications/my-alerts; all three read one permission store (helpers/push/pushPermission),
   * so a decision in one place shows everywhere without a reload.
   */
  const open = Boolean(anchorEl);
  const dayGroups = useMemo(() => groupByDay(notifications), [notifications]);

  return (
    <>
      <IconButton
        className="notif-bell-button"
        aria-label="notifications"
        onClick={handleBellClick}
      >
        <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} max={99} color="error">
          <NotificationsNoneOutlinedIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        classes={{ paper: 'notif-popover-paper' }}
      >
        <Box className="notif-popover-header">
          <Box className="notif-popover-icon">
            <NotificationsNoneOutlinedIcon sx={{ fontSize: 17 }} />
          </Box>
          <Box>
            <Typography className="notif-popover-title">Notifications</Typography>
            <Typography className="notif-popover-subtitle">
              Platform events scoped to your role
            </Typography>
          </Box>
          {notifications.length > 0 && (
            <Typography className="notif-popover-count">{notifications.length}</Typography>
          )}
        </Box>

        {/* The house loading state. A spinner says "wait"; a skeleton says what is coming, which
            for a list of notifications is the more useful thing to say. */}
        {loading ? (
          <SkeletonLoader rows={4} />
        ) : notifications.length === 0 ? (
          <EmptyState
            compact
            icon={<NotificationsNoneOutlinedIcon />}
            title="You're all caught up"
          />
        ) : (
          <Box className="notif-list">
            {dayGroups.map((group) => (
              <Box key={group.day} className="notif-day-group">
                <Typography className="notif-day-label">{group.day}</Typography>
                <Box className="notif-day-card">
                  {group.items.map((notif) => {
                    const { Icon, tone, tint } = TYPE_PRESENTATION[notif.type] ?? DEFAULT_PRESENTATION;
                    return (
                      <Box
                        key={notif.id}
                        className={`notif-item${!notif.isRead ? ' notif-item--unread' : ''}`}
                        onClick={() => handleItemClick(notif)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleItemClick(notif)}
                      >
                        <Box className="notif-item-tile" sx={{ backgroundColor: tint }}>
                          <Icon sx={{ fontSize: 17, color: tone }} />
                        </Box>
                        <Box className="notif-item-content">
                          <Typography className="notif-item-title">{feedTitle(notif)}</Typography>
                          {notif.body && !(notif.count && notif.count > 1) && (
                            <Typography className="notif-item-body">{notif.body}</Typography>
                          )}
                        </Box>
                        {notif.createdAt && (
                          <Typography className="notif-item-time">
                            {formatClockTime(notif.createdAt)}
                          </Typography>
                        )}
                        {!notif.isRead && <Box className="notif-unread-dot" />}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        )}

      </Popover>
    </>
  );
};

/** Backend timestamps arrive without a zone marker; treat those as UTC. */
function parseTimestamp(isoString: string): Date {
  return new Date(/Z$|[+-]\d{2}:\d{2}$/.test(isoString) ? isoString : isoString + 'Z');
}

/** Rows are grouped by day, so the row itself only needs the clock time. */
function formatClockTime(isoString: string): string {
  try {
    // The console's clock format, as in formatDateTime: "9:37 pm".
    return parseDate(isoString)?.format('h:mm a') ?? '';
  } catch {
    return '';
  }
}

/** "Today" / "Yesterday" / an absolute date, matching the v2 feed. */
function dayLabel(date: Date): string {
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(new Date()) - startOf(date)) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return formatDate(date);
}

/** A feed row, which may stand for several collapsed notifications of one type. */
export type FeedItem = AdminNotification & { count?: number };

/** Event types that arrive in high volume and bury everything else; collapsed per day. */
const COLLAPSED_TYPES: Record<string, { href: string; plural: string }> = {
  NEW_JOBSEEKER_SIGNUP: { href: '/jobseekers', plural: 'new job-seeker sign-ups' },
};

/**
 * Fold high-volume runs into one summary row per day, keeping the first occurrence's place
 * so the quieter, important notifications around them stay visible instead of being pushed
 * down a wall of sign-ups (owner: "we see too many job-seeker sign ups, we miss some other
 * important notifications"). A lone event of a collapsed type is left as-is.
 */
export function collapseHighVolume(items: AdminNotification[]): FeedItem[] {
  const out: FeedItem[] = [];
  const summaryIndex = new Map<string, number>();
  for (const item of items) {
    const collapse = COLLAPSED_TYPES[item.type];
    if (!collapse) {
      out.push(item);
      continue;
    }
    const existing = summaryIndex.get(item.type);
    if (existing === undefined) {
      summaryIndex.set(item.type, out.length);
      out.push({ ...item, count: 1 });
    } else {
      const summary = out[existing];
      summary.count = (summary.count ?? 1) + 1;
      // Keep the whole run read only when every event in it is read.
      summary.isRead = summary.isRead && item.isRead;
    }
  }
  // Point a real summary at the list; a single event keeps its own link.
  for (const item of out) {
    if (item.count && item.count > 1) item.link = COLLAPSED_TYPES[item.type].href;
  }
  return out;
}

/** The summary row's title, e.g. "12 new job-seeker sign-ups". */
export function feedTitle(item: FeedItem): string {
  if (item.count && item.count > 1) return `${item.count} ${COLLAPSED_TYPES[item.type].plural}`;
  return item.title;
}

/**
 * Group the feed by day, preserving the server's ordering. Anything without a
 * usable timestamp collects under "Earlier" rather than being dropped. High-volume
 * types are collapsed within each day so one busy type can't bury the rest.
 */
function groupByDay(items: AdminNotification[]): { day: string; items: FeedItem[] }[] {
  const groups: { day: string; items: AdminNotification[] }[] = [];
  for (const item of items) {
    let label = 'Earlier';
    if (item.createdAt) {
      const date = parseTimestamp(item.createdAt);
      if (!Number.isNaN(date.getTime())) label = dayLabel(date);
    }
    const last = groups[groups.length - 1];
    if (last && last.day === label) last.items.push(item);
    else groups.push({ day: label, items: [item] });
  }
  return groups.map((g) => ({ day: g.day, items: collapseHighVolume(g.items) }));
}

const mapStateToProps = (state: RootState) => ({
  userId: state.loginUserProfile?.profile?._id as string | undefined,
});

export default connect(mapStateToProps)(NotificationBellComponent);
